import { analyzeAudioFile, type AnalysisResult } from './analyzer/index';

const isEmbedded = new URLSearchParams(window.location.search).get('embed') === '1';
const allowedParentOrigins = new Set([
  'https://orbitalaudiolabel.com',
  'https://www.orbitalaudiolabel.com',
  'https://test-site.orbitalaudiolabel.com',
]);

function getParentOrigin() {
  if (!document.referrer) return null;

  try {
    const origin = new URL(document.referrer).origin;
    return allowedParentOrigins.has(origin) ? origin : null;
  } catch {
    return null;
  }
}

if (isEmbedded) {
  document.documentElement.classList.add('is-embedded');
}

document.addEventListener('DOMContentLoaded', () => {
  const dropZone = document.getElementById('dropZone')!;
  const fileInput = document.getElementById('fileInput') as HTMLInputElement;
  const dropZoneContent = document.getElementById('dropZoneContent')!;
  const dropZoneProgress = document.getElementById('dropZoneProgress')!;
  const progressRingFill = document.querySelector('#progressRingFill') as SVGCircleElement;
  const statusMessage = document.getElementById('statusMessage')!;
  const fileNameEl = document.getElementById('fileName')!;
  const results = document.getElementById('results')!;
  const bpmValue = document.getElementById('bpmValue')!;
  const bpmMeta = document.getElementById('bpmMeta')!;
  const keyValue = document.getElementById('keyValue')!;
  const keyMeta = document.getElementById('keyMeta')!;
  const copyBtn = document.getElementById('copyBtn')!;
  const resetBtn = document.getElementById('resetBtn')!;
  const errorToast = document.getElementById('errorToast')!;
  const errorText = document.getElementById('errorText')!;

  const CIRCUMFERENCE = 2 * Math.PI * 16; // r=16
  const parentOrigin = getParentOrigin();

  function notifyParentOfHeight() {
    if (!isEmbedded || window.parent === window || !parentOrigin) return;

    window.parent.postMessage(
      {
        type: 'orbital-keybpm:resize',
        height: Math.ceil(document.documentElement.scrollHeight),
      },
      parentOrigin,
    );
  }

  function setProgress(percent: number) {
    const offset = CIRCUMFERENCE - (percent / 100) * CIRCUMFERENCE;
    progressRingFill.style.strokeDashoffset = `${offset}`;
  }

  function showError(msg: string) {
    errorText.textContent = msg;
    errorToast.hidden = false;
  }

  function hideError() {
    errorToast.hidden = true;
  }

  function resetUI() {
    dropZoneContent.hidden = false;
    dropZoneProgress.hidden = true;
    results.hidden = true;
    hideError();
    setProgress(0);
    fileInput.value = '';
    requestAnimationFrame(notifyParentOfHeight);
  }

  async function handleFile(file: File) {
    if (!file) return;
    const validTypes = [
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/mp3',
    ];
    const validExts = ['.mp3', '.wav'];
    const isValidType = validTypes.includes(file.type);
    const isValidExt = validExts.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!isValidType && !isValidExt) {
      showError('Please drop an MP3 or WAV file.');
      return;
    }

    hideError();
    dropZoneContent.hidden = true;
    dropZoneProgress.hidden = false;
    results.hidden = true;
    fileNameEl.textContent = file.name;
    statusMessage.textContent = 'Decoding…';
    setProgress(20);

    try {
      statusMessage.textContent = 'Analyzing tempo…';
      setProgress(55);
      // Allow UI paint
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      const result = await analyzeAudioFile(file);

      statusMessage.textContent = 'Analyzing key…';
      setProgress(90);
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      statusMessage.textContent = 'Done';
      setProgress(100);
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 120));

      renderResult(result);
      dropZoneProgress.hidden = true;
      results.hidden = false;
      requestAnimationFrame(notifyParentOfHeight);
    } catch (err: any) {
      showError(err?.message ?? 'Analysis failed. Try another file.');
      dropZoneProgress.hidden = true;
      dropZoneContent.hidden = false;
      requestAnimationFrame(notifyParentOfHeight);
    }
  }

  function renderResult(r: AnalysisResult) {
    bpmValue.textContent = `${r.bpm} BPM`;
    bpmMeta.textContent = r.bpmConfidence > 0 ? `Confidence ${Math.round(r.bpmConfidence * 100)}%` : '';
    keyValue.textContent = r.key;
    keyMeta.textContent = r.keyConfidence > 0 ? `Confidence ${Math.round(r.keyConfidence * 100)}%` : '';
  }

  // Events
  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('drop-zone--over');
  });
  dropZone.addEventListener('dragleave', () => {
    dropZone.classList.remove('drop-zone--over');
  });
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('drop-zone--over');
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  });
  dropZone.addEventListener('click', () => {
    fileInput.click();
  });
  dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) handleFile(file);
  });

  resetBtn.addEventListener('click', resetUI);
  copyBtn.addEventListener('click', async () => {
    const bpm = bpmValue.textContent ?? '';
    const key = keyValue.textContent ?? '';
    const text = `${bpm}, Key: ${key}`;
    try {
      await navigator.clipboard.writeText(text);
      const original = copyBtn.textContent;
      copyBtn.textContent = 'Copied';
      setTimeout(() => (copyBtn.textContent = original), 1200);
    } catch {
      // ignore
    }
  });

  if (isEmbedded) {
    const resizeObserver = new ResizeObserver(notifyParentOfHeight);
    resizeObserver.observe(document.body);
    window.addEventListener('load', notifyParentOfHeight, { once: true });
    notifyParentOfHeight();
  }
});
